/**
 * Personal analytics dashboard built from server training analytics and activity data. Derives
 * time-range series, correlations, duration-error trends, field coverage, and model-improvement
 * displays; supports raw training JSON export. Correlation and evidence summaries are descriptive
 * rather than causal claims.
 *
 * @module apps/web/src/pages/Analytics
 */

import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useRouteTasks } from '@/stores/routeData';
import { api, type TrainingAnalyticsData, type TrainingExportData } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Activity,
  AlertCircle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  BrainCircuit,
  CheckCircle2,
  Clock,
  Database,
  Download,
  Gauge,
  Layers3,
  LineChart,
  Network,
  RefreshCw,
  Sigma,
  Sparkles,
} from 'lucide-react';

/** Rolling analytics window choices; labels are mapped separately from the day-count filtering implementation. */
type TimeRange = 'week' | 'month' | 'quarter' | 'year' | 'all';
/** Available representations of the same selected numeric-series pair. */
type ChartMode = 'bar' | 'line' | 'scatter' | 'heatmap';

/** Preformatted statistic, explanatory caption, and icon displayed in the analytics summary. */
interface MetricCard {
  /** Short title shown for this statistic or learning stage. */
  label: string;
  /** Already formatted value shown prominently in the panel. */
  value: string;
  /** Explanatory caption qualifying the displayed value. */
  detail: string;
  /** Lucide icon component used to identify the statistic visually. */
  icon: typeof Activity;
}

/** Stable metric identity and activity-index-aligned numeric observations; null preserves missing samples. */
interface NumericSeries {
  /** Stable metric key used by chart selectors and correlation joins. */
  key: string;
  /** Human-readable metric name shown on axes and controls. */
  label: string;
  /** Activity-index-aligned numeric observations; null preserves missing evidence. */
  values: Array<number | null>;
}

/** Normalized activity projection for analytics joins, filtering, and charts; nullable observations must remain distinct from measured zero. */
interface ActivitySample {
  /** Activity identifier used for analytics joins and row identity. */
  id: string;
  /** Activity title retained for labels and examples. */
  title: string;
  /** Normalized lifecycle value used by filters and completion summaries. */
  status: string;
  /** Normalized bundle label used to group activity observations. */
  bundle: string;
  /** Normalized project label used to group activity observations. */
  project: string;
  /** Normalized assignee label used for descriptive grouping. */
  assignee: string;
  /** Numeric activity priority signal, or null when absent. */
  priorityScore: number | null;
  /** Estimated total duration in minutes, or null when unknown. */
  durationMinutes: number | null;
  /** Estimated remaining duration in minutes, or null when unknown. */
  remainingMinutes: number | null;
  /** Reported completion percentage, or null when unavailable. */
  completionPercent: number | null;
  /** Assigned start timestamp, or null for unscheduled activity. */
  scheduledStart: string | null;
  /** Assigned finish timestamp, or null for unscheduled activity. */
  scheduledEnd: string | null;
  /** Deadline timestamp, or null when no deadline is stored. */
  dueDate: string | null;
  /** Creation timestamp used for range filtering when available. */
  createdAt: string | null;
  /** Modification timestamp used for range filtering when available. */
  updatedAt: string | null;
  /** Productivity investment score, or null when unobserved. */
  requiredP: number | null;
  /** Rejuvenation investment score, or null when unobserved. */
  requiredRJ: number | null;
  /** Productivity return score, or null when unobserved. */
  returnP: number | null;
  /** Rejuvenation return score, or null when unobserved. */
  returnRJ: number | null;
  /** Start-resistance score, or null when unobserved. */
  resistance: number | null;
  /** Extracted focus score, or null when no numeric focus value is available. */
  focus: number | null;
  /** Normalized emotion label used for descriptive grouping. */
  emotion: string;
  /** Parsed custom values used to build additional numeric series. */
  customAttributes: Record<string, unknown>;
}

/** Loaded activity projections, aggregate training analytics, and optional raw training export used by the page. */
interface AnalyticsBundle {
  /** Normalized activity records currently loaded for analysis. */
  activities: ActivitySample[];
  /** Server aggregate learning diagnostics, or null before loading or on failure. */
  apiAnalytics: TrainingAnalyticsData | null;
  /** Raw export loaded on demand, or null before an export is available. */
  exportedTraining: TrainingExportData | null;
}

/** One duration-error bucket with mean absolute error, sample count, and signed bias. */
interface TrendPoint {
  /** Time-bucket label used to order and display duration errors. */
  bucket: string;
  /** Mean absolute duration error in minutes for this bucket. */
  mae: number;
  /** Number of observations contributing to the bucket. */
  samples: number;
  /** Mean signed duration error in minutes for this bucket. */
  bias: number;
}

/** Heuristic readiness-stage label, explanation, and bounded progress percentage. */
interface LearningStage {
  /** Short title shown for this statistic or learning stage. */
  label: string;
  /** Already formatted value shown prominently in the panel. */
  value: string;
  /** Explanatory caption qualifying the displayed value. */
  detail: string;
  /** Heuristic stage completion percentage, bounded from 0 to 100. */
  progress: number;
}

/** Shared panel row for duration-error association or aggregate field coverage. metricUnit identifies whether mae/bias carry minute errors or percent/numeric-count presentation values. */
interface FieldImpactRow {
  /** Stable field or diagnostic-row key used in rendering. */
  key: string;
  /** Human-readable field or evidence-group label. */
  label: string;
  /** Observation count associated with this field's displayed diagnostic. */
  samples: number;
  /** Minute error for error rows, or percent coverage when metricUnit is percent. */
  mae: number;
  /** Signed minute error for error rows, or numeric-value count in coverage rows. */
  bias: number;
  /** Illustrative value or explanatory text accompanying the field diagnostic. */
  example: string;
  /** Presentation unit distinguishing minute-error rows from percent-coverage rows. */
  metricUnit?: string;
}

/** Derived dashboard metrics and recommendations assembled from observed errors and coverage heuristics; it is not a model-training result. */
interface ModelImprovementSummary {
  /** Dashboard-derived accuracy score computed from observed error summaries. */
  accuracyScore: number;
  /** Mean absolute duration error in minutes. */
  durationMae: number;
  /** Root mean squared duration error in minutes. */
  rmse: number;
  /** Mean signed duration error in minutes. */
  bias: number;
  /** Number of duration observations supporting the summary. */
  sampleCount: number;
  /** Heuristic data-readiness score derived from evidence coverage. */
  readinessScore: number;
  /** Time-bucketed duration-error points used in the learning trend. */
  trendRows: TrendPoint[];
  /** Change between compared error buckets in minutes, or null without a comparison. */
  trendDeltaMinutes: number | null;
  /** Relative error change between compared buckets, or null without a comparison. */
  trendDeltaPercent: number | null;
  /** Heuristic learning stages and their bounded completion percentages. */
  stages: LearningStage[];
  /** Descriptive error/coverage rows; associations do not establish causation. */
  fieldImpacts: FieldImpactRow[];
  /** Suggestions generated from the current evidence and error summary. */
  recommendations: string[];
}

/** Empty analytics state before data is loaded or after the owner resets the page. */
const EMPTY_BUNDLE: AnalyticsBundle = {
  activities: [],
  apiAnalytics: null,
  exportedTraining: null,
};

/** Human-facing names for analytics range selectors; filtering uses rolling day counts. */
const rangeLabels: Record<TimeRange, string> = {
  week: 'This week',
  month: 'This month',
  quarter: 'This quarter',
  year: 'This year',
  all: 'All time',
};

/**
 * Personal analytics dashboard built from server training analytics and activity data. Derives
 * time-range series, correlations, duration-error trends, field coverage, and model-improvement
 * displays; supports raw training JSON export. Correlation and evidence summaries are descriptive
 * rather than causal claims.
 */
export function Analytics() {
  const tasks = useRouteTasks();
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [chartMode, setChartMode] = useState<ChartMode>('bar');
  const [xMetric, setXMetric] = useState('hour');
  const [yMetric, setYMetric] = useState('duration');
  const [analysisQuery, setAnalysisQuery] = useState('correlate duration completion');
  const [bundle, setBundle] = useState<AnalyticsBundle>(EMPTY_BUNDLE);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const reloadAnalytics = useEffectEvent(() => {
    void loadAnalytics();
  });
  useEffect(() => {
    reloadAnalytics();
  }, [tasks]);

  async function loadAnalytics() {
    setLoading(true);
    setError('');
    try {
      const localActivities = normalizeActivities(tasks);
      const analyticsResponse = await api.training.getAnalytics();
      setBundle({
        activities: localActivities,
        apiAnalytics: analyticsResponse.success ? analyticsResponse.data || null : null,
        exportedTraining: null,
      });
      if (!analyticsResponse.success) {
        setError(analyticsResponse.error?.message || 'Backend analytics unavailable.');
      }
    } catch (cause) {
      setBundle({
        ...EMPTY_BUNDLE,
        activities: normalizeActivities(tasks),
      });
      setError(cause instanceof Error ? cause.message : 'Analytics could not be loaded.');
    } finally {
      setLoading(false);
    }
  }

  async function exportTrainingJson() {
    setExporting(true);
    setError('');
    try {
      const response = await api.training.exportTrainingData({ format: 'json', includeRaw: true });
      if (!response.success) {
        setError(response.error?.message || 'Training export could not be generated.');
        return;
      }
      const data =
        response.data && typeof response.data === 'object' && !Array.isArray(response.data)
          ? (response.data as TrainingExportData)
          : {};
      setBundle(current => ({ ...current, exportedTraining: data }));
      downloadJson(data);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Training export could not be generated.');
    } finally {
      setExporting(false);
    }
  }

  const filteredActivities = useMemo(
    () => filterByRange(bundle.activities, timeRange),
    [bundle.activities, timeRange]
  );

  const series = useMemo(
    () => buildNumericSeries(filteredActivities, bundle.exportedTraining),
    [filteredActivities, bundle.exportedTraining]
  );

  const metricOptions = series.map(item => ({ value: item.key, label: item.label }));
  const xSeries = series.find(item => item.key === xMetric) || series[0];
  const ySeries = series.find(item => item.key === yMetric) || series[1] || series[0];
  const correlations = useMemo(() => buildCorrelations(series), [series]);
  const commandResult = useMemo(
    () => runAnalysisQuery(analysisQuery, series, filteredActivities),
    [analysisQuery, series, filteredActivities]
  );
  const improvement = useMemo(
    () => buildModelImprovement(bundle.apiAnalytics, bundle.exportedTraining, filteredActivities),
    [bundle.apiAnalytics, bundle.exportedTraining, filteredActivities]
  );
  const modelError = bundle.apiAnalytics?.modelError || {};
  const trainingShape = bundle.apiAnalytics?.trainingDataShape || {};

  const completed = filteredActivities.filter(activity => isCompleted(activity.status));
  const metricCards: MetricCard[] = [
    {
      label: 'Activities',
      value: String(filteredActivities.length),
      detail: `${completed.length} completed in ${rangeLabels[timeRange].toLowerCase()}`,
      icon: Activity,
    },
    {
      label: 'Completion',
      value: `${formatNumber(percent(completed.length, filteredActivities.length), 1)}%`,
      detail: 'Share of visible activities completed',
      icon: Sigma,
    },
    {
      label: 'Avg duration',
      value: `${formatNumber(average(filteredActivities.map(activity => activity.durationMinutes)), 0)}m`,
      detail: 'Estimated or actual duration where present',
      icon: Clock,
    },
    {
      label: 'Duration MAE',
      value: `${formatNumber(modelError.duration?.maeMinutes, 1)}m`,
      detail: `${modelError.duration?.sampleCount || 0} duration training samples`,
      icon: BrainCircuit,
    },
  ];

  return (
    <div className="min-h-full px-3 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-5">
        <section className="bg-card/90 rounded-md border p-5 shadow-sm backdrop-blur-xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Badge variant="outline" className="mb-3 rounded-md">
                Analytics engine
              </Badge>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                Activity analytics
              </h1>
              <p className="text-muted-foreground mt-2 max-w-3xl text-sm">
                Explore activity, energy, emotion, duration, custom field, and model-error data. The
                page is defensive by design: partial rows become unknown values instead of crashing
                the app.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={timeRange} onValueChange={value => setTimeRange(value as TimeRange)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(rangeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadAnalytics} disabled={loading}>
                <RefreshCw className={loading ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'} />
                Refresh
              </Button>
            </div>
          </div>
          {error && (
            <div className="border-destructive/30 bg-destructive/10 text-destructive mt-4 rounded-md border px-3 py-2 text-sm">
              {error}
            </div>
          )}
        </section>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {metricCards.map(card => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className="rounded-md">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                  <CardTitle className="text-sm font-medium">{card.label}</CardTitle>
                  <Icon className="text-muted-foreground h-4 w-4" />
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl font-semibold">{card.value}</div>
                  <p className="text-muted-foreground mt-1 text-xs">{card.detail}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <ModelImprovementVisualizer summary={improvement} />

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.55fr)]">
          <Card className="rounded-md">
            <CardHeader className="p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3 className="h-4 w-4" />
                    Graph workspace
                  </CardTitle>
                  <CardDescription>
                    Switch chart type and compare any numeric metric.
                  </CardDescription>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  <Select
                    value={chartMode}
                    onValueChange={value => setChartMode(value as ChartMode)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bar">Bar</SelectItem>
                      <SelectItem value="line">Line</SelectItem>
                      <SelectItem value="scatter">Scatter</SelectItem>
                      <SelectItem value="heatmap">Heatmap</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={xMetric} onValueChange={setXMetric}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {metricOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={yMetric} onValueChange={setYMetric}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {metricOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {filteredActivities.length === 0 ? (
                <EmptyAnalyticsState />
              ) : (
                <MetricChart mode={chartMode} xSeries={xSeries} ySeries={ySeries} />
              )}
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Network className="h-4 w-4" />
                Correlations
              </CardTitle>
              <CardDescription>Top Pearson correlations from visible metrics.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4 pt-0">
              {correlations.length === 0 ? (
                <p className="bg-muted/30 text-muted-foreground rounded-md border p-3 text-sm">
                  More numeric samples are needed before correlations are meaningful.
                </p>
              ) : (
                correlations.slice(0, 8).map(item => (
                  <div key={`${item.a}:${item.b}`} className="rounded-md border p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate font-medium">
                        {item.aLabel} vs {item.bLabel}
                      </span>
                      <Badge
                        variant={Math.abs(item.r) >= 0.65 ? 'default' : 'outline'}
                        className="rounded-md"
                      >
                        r {formatNumber(item.r, 2)}
                      </Badge>
                    </div>
                    <div className="bg-muted mt-2 h-2 overflow-hidden rounded-full">
                      <div
                        className={item.r >= 0 ? 'bg-primary h-full' : 'bg-destructive h-full'}
                        style={{ width: `${Math.min(100, Math.abs(item.r) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
          <Card className="rounded-md">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <LineChart className="h-4 w-4" />
                Model error and training shape
              </CardTitle>
              <CardDescription>
                Prediction error by sample class, week, and collected dataset.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-4 pt-0">
              <div className="grid gap-3 sm:grid-cols-3">
                <StatPill
                  label="Duration samples"
                  value={trainingShape.durationSamples || modelError.duration?.sampleCount || 0}
                />
                <StatPill label="Feedback rows" value={trainingShape.feedbackSamples || 0} />
                <StatPill label="Custom fields" value={trainingShape.customFields || 0} />
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                <ErrorList
                  title="Error by activity class"
                  rows={modelError.duration?.byClass || []}
                  empty="No class-level duration errors yet."
                />
                <ErrorList
                  title="Weekly error trend"
                  rows={modelError.duration?.trend || []}
                  empty="No duration trend yet."
                />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sigma className="h-4 w-4" />
                Analysis console
              </CardTitle>
              <CardDescription>Small analysis grammar for quick queries.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4 pt-0">
              <Label htmlFor="analysisQuery">Command</Label>
              <Textarea
                id="analysisQuery"
                value={analysisQuery}
                onChange={event => setAnalysisQuery(event.target.value)}
                className="min-h-24 font-mono text-xs"
                placeholder="correlate duration completion"
              />
              <div className="bg-muted/30 rounded-md border p-3 font-mono text-xs leading-6">
                {commandResult}
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  'correlate duration completion',
                  'average duration by bundle',
                  'average p_return by emotion',
                ].map(command => (
                  <Button
                    key={command}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAnalysisQuery(command)}
                  >
                    {command}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="rounded-md">
          <CardHeader className="p-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4" />
              Training data export
            </CardTitle>
            <CardDescription>
              Export raw and derived training data for external modeling: specificities, custom
              fields, predictions, actuals, deltas, sessions, and activity metadata.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 p-4 pt-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-muted-foreground grid gap-2 text-sm sm:grid-cols-3">
              <span>{trainingShape.tasks || filteredActivities.length} activities</span>
              <span>{trainingShape.sessions || 0} sessions</span>
              <span>{trainingShape.energySamples || 0} energy samples</span>
            </div>
            <Button variant="outline" onClick={exportTrainingJson} disabled={exporting}>
              <Download className={exporting ? 'mr-2 h-4 w-4 animate-pulse' : 'mr-2 h-4 w-4'} />
              {exporting ? 'Exporting' : 'Export JSON'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** Explain why graphs are absent when the selected range has no usable analytics samples; invite session logging without inventing zero-valued observations. */
function EmptyAnalyticsState() {
  return (
    <div className="bg-muted/25 flex min-h-[320px] flex-col items-center justify-center rounded-md border border-dashed p-6 text-center">
      <AlertCircle className="text-muted-foreground mb-3 h-10 w-10" />
      <h3 className="text-base font-medium">No analytics samples in this range</h3>
      <p className="text-muted-foreground mt-2 max-w-md text-sm">
        Complete or log sessions, then this view will populate graphs, correlations, and prediction
        error.
      </p>
    </div>
  );
}

/** Compose error metrics, readiness stages, weekly trends, and field coverage from a normalized model summary. These descriptive signals are not a causal measurement of training improvement. */
function ModelImprovementVisualizer({ summary }: { /** Normalized analytics summary containing sample counts, error statistics, readiness, trend buckets, and recommendations. */
summary: ModelImprovementSummary }) {
  const improving = summary.trendDeltaMinutes !== null && summary.trendDeltaMinutes >= 0;
  const DeltaIcon = improving ? ArrowDownRight : ArrowUpRight;
  const trendLabel =
    summary.trendDeltaMinutes === null
      ? 'Needs trend'
      : `${improving ? '-' : '+'}${formatNumber(Math.abs(summary.trendDeltaMinutes), 1)}m recent MAE`;

  return (
    <Card className="border-primary/20 bg-card/95 overflow-hidden rounded-md shadow-sm">
      <CardHeader className="bg-background/45 border-b p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="text-primary h-4 w-4" />
              Model improvement
            </CardTitle>
            <CardDescription>
              Follow how training data is reducing prediction error and which signals are carrying
              weight.
            </CardDescription>
          </div>
          <Badge variant="outline" className="w-fit rounded-md">
            {summary.sampleCount} duration sample{summary.sampleCount === 1 ? '' : 's'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1.18fr)_minmax(320px,0.82fr)]">
        <div className="space-y-4">
          <div className="grid gap-3">
            <ModelHealthGauge score={summary.accuracyScore} readiness={summary.readinessScore} />
            <div className="grid gap-3 sm:grid-cols-3">
              <SignalTile
                label="Duration MAE"
                value={`${formatNumber(summary.durationMae, 1)}m`}
                detail="Average absolute error"
                icon={Gauge}
              />
              <SignalTile
                label="RMSE"
                value={`${formatNumber(summary.rmse, 1)}m`}
                detail="Penalizes large misses"
                icon={LineChart}
              />
              <SignalTile
                label="Bias"
                value={formatSignedMinutes(summary.bias)}
                detail="Actual minus estimate"
                icon={BrainCircuit}
              />
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {summary.stages.map((stage, index) => (
              <div key={stage.label} className="bg-background/70 rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="bg-primary/12 text-primary flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-semibold">
                    {index + 1}
                  </span>
                  {stage.progress >= 100 ? (
                    <CheckCircle2 className="text-primary h-4 w-4" />
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      {formatNumber(stage.progress, 0)}%
                    </span>
                  )}
                </div>
                <div className="mt-3 text-sm font-medium">{stage.label}</div>
                <div className="text-muted-foreground mt-1 text-xs">{stage.value}</div>
                <div className="bg-muted mt-3 h-1.5 overflow-hidden rounded-full">
                  <div
                    className="bg-primary h-full rounded-full"
                    style={{ width: `${stage.progress}%` }}
                  />
                </div>
                <p className="text-muted-foreground mt-2 min-h-8 text-xs">{stage.detail}</p>
              </div>
            ))}
          </div>

          <div className="bg-background/70 rounded-md border p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-medium">Error trend</div>
                <div className="text-muted-foreground text-xs">
                  Weekly duration MAE from training rows.
                </div>
              </div>
              <Badge variant={improving ? 'default' : 'outline'} className="rounded-md">
                <DeltaIcon className="mr-1 h-3.5 w-3.5" />
                {trendLabel}
              </Badge>
            </div>
            <TrendSparkline rows={summary.trendRows} />
          </div>
        </div>

        <div className="grid content-start gap-4 lg:grid-cols-2 xl:grid-cols-1">
          <FieldImpactPanel rows={summary.fieldImpacts} />
          <div className="bg-background/70 rounded-md border p-3">
            <div className="mb-3 flex items-center gap-2">
              <Layers3 className="text-primary h-4 w-4" />
              <div>
                <div className="text-sm font-medium">Training next steps</div>
                <div className="text-muted-foreground text-xs">
                  Generated from current model coverage.
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {summary.recommendations.map((item, index) => (
                <div key={`${item}:${index}`} className="bg-muted/20 rounded-md border p-2 text-sm">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Render the supplied accuracy score as a clamped circular gauge alongside data readiness. Display labels use the supplied scores; this view does not evaluate or train a model. */
function ModelHealthGauge({ score, readiness }: { /** Derived accuracy percentage; only the painted arc is clamped to 0–100. */
score: number; /** Derived data-readiness percentage shown separately from accuracy. */
readiness: number }) {
  return (
    <div className="bg-background/70 rounded-md border p-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">Learning health</div>
          <div className="text-muted-foreground text-xs">Accuracy and data readiness.</div>
        </div>
        <Badge variant="secondary" className="rounded-md">
          {formatNumber(readiness, 0)}% ready
        </Badge>
      </div>
      <div className="flex items-center gap-4">
        <div
          className="grid h-28 w-28 shrink-0 place-items-center rounded-full"
          style={{
            background: `conic-gradient(hsl(var(--primary)) ${clamp(score, 0, 100) * 3.6}deg, hsl(var(--muted)) 0deg)`,
          }}
          aria-label={`Scheduling accuracy ${formatNumber(score, 0)} percent`}
        >
          <div className="bg-card grid h-[86px] w-[86px] place-items-center rounded-full border text-center">
            <span className="text-2xl font-semibold">{formatNumber(score, 0)}%</span>
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium">
            {score >= 75 ? 'Reliable signal' : score >= 45 ? 'Learning signal' : 'Early signal'}
          </div>
          <p className="text-muted-foreground mt-1 text-xs leading-5">
            More completed sessions improve duration predictions, confidence, and class-level error
            estimates.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Display one preformatted model metric with a short explanation of its meaning. */
function SignalTile({
  label,
  value,
  detail,
  icon: Icon,
}: {
  /** Visible caption describing this control or metric. */
  label: string;
  /** Preformatted metric value displayed without additional measurement. */
  value: string;
  /** Explanation of the statistic, such as average absolute error or signed bias. */
  detail: string;
  /** Icon component rendered beside the visible caption. */
  icon: typeof Activity;
}) {
  return (
    <div className="bg-background/70 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs">{label}</span>
        <Icon className="text-muted-foreground h-4 w-4" />
      </div>
      <div className="mt-2 text-xl font-semibold">{value}</div>
      <div className="text-muted-foreground mt-1 text-xs">{detail}</div>
    </div>
  );
}

/** Plot ordered weekly duration-error buckets with a guarded scale for constant values. Fewer than two buckets produces an evidence-needed message instead of a trend line. */
function TrendSparkline({ rows }: { /** Chronologically ordered MAE buckets with their sample counts; at least two are needed for a line. */
rows: TrendPoint[] }) {
  if (rows.length < 2) {
    return (
      <div className="bg-muted/20 text-muted-foreground flex min-h-[132px] items-center justify-center rounded-md border border-dashed p-4 text-center text-sm">
        At least two trend buckets are needed to draw improvement over time.
      </div>
    );
  }

  const width = 560;
  const height = 132;
  const padding = 18;
  const values = rows.map(row => row.mae);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const xScale = (index: number) =>
    padding + (index / Math.max(1, rows.length - 1)) * (width - padding * 2);
  const yScale = (value: number) =>
    height - padding - ((value - min) / Math.max(1, max - min)) * (height - padding * 2);
  const points = rows.map((row, index) => `${xScale(index)},${yScale(row.mae)}`).join(' ');
  const last = rows[rows.length - 1]!;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="bg-card min-h-[132px] min-w-[480px] rounded-md border"
      >
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
          stroke="hsl(var(--border))"
        />
        <polyline
          points={points}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {rows.map((row, index) => (
          <circle
            key={`${row.bucket}:${index}`}
            cx={xScale(index)}
            cy={yScale(row.mae)}
            r={3.5}
            fill="hsl(var(--primary))"
            opacity={0.85}
          />
        ))}
        <text x={padding} y={18} fill="hsl(var(--muted-foreground))" fontSize="11">
          {formatNumber(max, 1)}m
        </text>
        <text
          x={width - padding}
          y={height - 8}
          fill="hsl(var(--muted-foreground))"
          fontSize="11"
          textAnchor="end"
        >
          {last.bucket} · {formatNumber(last.mae, 1)}m · n={last.samples}
        </text>
      </svg>
    </div>
  );
}

/** Show the first five normalized field-coverage rows and relative metric bars. The panel preserves each row’s unit; coverage and association do not establish a field’s causal effect. */
function FieldImpactPanel({ rows }: { /** Field coverage/error rows in the parent’s ranking order, including units and sample counts. */
rows: FieldImpactRow[] }) {
  const maxMae = Math.max(1, ...rows.map(row => row.mae));
  return (
    <div className="bg-background/70 rounded-md border p-3">
      <div className="mb-3 flex items-center gap-2">
        <Network className="text-primary h-4 w-4" />
        <div>
          <div className="text-sm font-medium">Field impact</div>
          <div className="text-muted-foreground text-xs">
            Custom fields ranked by model signal coverage.
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="bg-muted/20 text-muted-foreground rounded-md border border-dashed p-3 text-sm">
          No custom-field training signals are available yet.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.slice(0, 5).map(row => (
            <div key={row.key} className="bg-card rounded-md border p-3">
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="truncate font-medium">{row.label}</div>
                  <div className="text-muted-foreground truncate text-xs">
                    {row.samples} sample{row.samples === 1 ? '' : 's'} · example {row.example}
                  </div>
                </div>
                <div className="text-muted-foreground text-right text-xs">
                  <div className="text-foreground font-medium">
                    {formatNumber(row.mae, 1)}
                    {row.metricUnit || ''}
                  </div>
                  <div>
                    {row.metricUnit === '%'
                      ? `${formatNumber(row.bias, 0)} numeric`
                      : formatSignedMinutes(row.bias)}
                  </div>
                </div>
              </div>
              <div className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full">
                <div
                  className="bg-primary h-full rounded-full"
                  style={{ width: `${Math.min(100, (row.mae / maxMae) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Pair selected numeric series, reject missing/empty inputs, and dispatch to a scatter, heatmap, bar, or line representation. Bar/line modes group points by rounded X before rendering. */
function MetricChart({
  mode,
  xSeries,
  ySeries,
}: {
  /** Requested graph mode; bar and line aggregate paired points by rounded X. */
  mode: ChartMode;
  /** Optional selected X series; its absence renders the empty analytics state. */
  xSeries?: NumericSeries;
  /** Optional selected Y series; its absence renders the empty analytics state. */
  ySeries?: NumericSeries;
}) {
  const points = zipSeries(xSeries, ySeries);
  if (!xSeries || !ySeries || points.length === 0) return <EmptyAnalyticsState />;
  if (mode === 'heatmap')
    return <Heatmap points={points} xLabel={xSeries.label} yLabel={ySeries.label} />;
  if (mode === 'scatter')
    return <ScatterPlot points={points} xLabel={xSeries.label} yLabel={ySeries.label} />;
  const grouped = groupByRoundedX(points);
  return <SeriesSvg mode={mode} rows={grouped} xLabel={xSeries.label} yLabel={ySeries.label} />;
}

/** Render pre-aggregated X/Y rows as bars or a line with count labels. The caller supplies nonempty grouped rows; guarded denominators handle constant X or small Y ranges. */
function SeriesSvg({
  mode,
  rows,
  xLabel,
  yLabel,
}: {
  /** Whether grouped means are represented by bars or connected points. */
  mode: 'bar' | 'line';
  /** Nonempty grouped X values, mean Y values, and contributing sample counts. */
  rows: Array<{ /** Rounded horizontal bucket value used to position the plotted aggregate. */ x: number; /** Mean vertical value for observations in this horizontal bucket. */ y: number; /** Number of observations supporting the bucket's plotted mean. */ count: number }>;
  /** Display name of the numeric X series. */
  xLabel: string;
  /** Display name of the numeric Y series. */
  yLabel: string;
}) {
  const width = 840;
  const height = 320;
  const padding = 42;
  const maxY = Math.max(1, ...rows.map(row => row.y));
  const minX = Math.min(...rows.map(row => row.x));
  const maxX = Math.max(...rows.map(row => row.x));
  const xScale = (value: number) =>
    padding + ((value - minX) / Math.max(1, maxX - minX)) * (width - padding * 2);
  const yScale = (value: number) => height - padding - (value / maxY) * (height - padding * 2);
  const line = rows.map(row => `${xScale(row.x)},${yScale(row.y)}`).join(' ');

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="bg-background min-h-[320px] min-w-[720px] rounded-md border"
      >
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
          stroke="hsl(var(--border))"
        />
        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={height - padding}
          stroke="hsl(var(--border))"
        />
        <text x={padding} y={24} fill="hsl(var(--muted-foreground))" fontSize="12">
          {yLabel}
        </text>
        <text
          x={width - padding}
          y={height - 10}
          fill="hsl(var(--muted-foreground))"
          fontSize="12"
          textAnchor="end"
        >
          {xLabel}
        </text>
        {mode === 'bar' ? (
          rows.map((row, index) => {
            const x = xScale(row.x);
            const barWidth = Math.max(10, (width - padding * 2) / Math.max(1, rows.length) - 8);
            const y = yScale(row.y);
            return (
              <g key={`${row.x}:${index}`}>
                <rect
                  x={x - barWidth / 2}
                  y={y}
                  width={barWidth}
                  height={height - padding - y}
                  rx={4}
                  fill="hsl(var(--primary))"
                  opacity={0.82}
                />
                <text
                  x={x}
                  y={height - 24}
                  textAnchor="middle"
                  fill="hsl(var(--muted-foreground))"
                  fontSize="10"
                >
                  {row.x}
                </text>
              </g>
            );
          })
        ) : (
          <>
            <polyline
              points={line}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="3"
              strokeLinejoin="round"
            />
            {rows.map((row, index) => (
              <circle
                key={`${row.x}:${index}`}
                cx={xScale(row.x)}
                cy={yScale(row.y)}
                r={4}
                fill="hsl(var(--primary))"
              />
            ))}
          </>
        )}
      </svg>
    </div>
  );
}

/** Project paired numeric samples into an SVG scatter plot with denominator guards for constant axes. The parent excludes empty input before mounting this view. */
function ScatterPlot({
  points,
  xLabel,
  yLabel,
}: {
  /** Nonempty paired numeric samples produced by the series join. */
  points: Array<{ /** Finite value of the selected horizontal metric for a paired observation. */ x: number; /** Finite value of the selected vertical metric from the same activity index. */ y: number }>;
  /** Display name of the numeric X series. */
  xLabel: string;
  /** Display name of the numeric Y series. */
  yLabel: string;
}) {
  const width = 840;
  const height = 320;
  const padding = 42;
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const xScale = (value: number) =>
    padding + ((value - minX) / Math.max(1, maxX - minX)) * (width - padding * 2);
  const yScale = (value: number) =>
    height - padding - ((value - minY) / Math.max(1, maxY - minY)) * (height - padding * 2);

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="bg-background min-h-[320px] min-w-[720px] rounded-md border"
      >
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
          stroke="hsl(var(--border))"
        />
        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={height - padding}
          stroke="hsl(var(--border))"
        />
        <text x={padding} y={24} fill="hsl(var(--muted-foreground))" fontSize="12">
          {yLabel}
        </text>
        <text
          x={width - padding}
          y={height - 10}
          fill="hsl(var(--muted-foreground))"
          fontSize="12"
          textAnchor="end"
        >
          {xLabel}
        </text>
        {points.map((point, index) => (
          <circle
            key={`${point.x}:${point.y}:${index}`}
            cx={xScale(point.x)}
            cy={yScale(point.y)}
            r={4}
            fill="hsl(var(--primary))"
            opacity={0.7}
          />
        ))}
      </svg>
    </div>
  );
}

/** Round paired X/Y samples into count buckets and display relative density. Rounding intentionally trades precision for a compact frequency view. */
function Heatmap({
  points,
  xLabel,
  yLabel,
}: {
  /** Paired numeric samples whose rounded coordinates define frequency buckets. */
  points: Array<{ /** Finite value of the selected horizontal metric for a paired observation. */ x: number; /** Finite value of the selected vertical metric from the same activity index. */ y: number }>;
  /** Display name of the numeric X series. */
  xLabel: string;
  /** Display name of the numeric Y series. */
  yLabel: string;
}) {
  const buckets = new Map<string, number>();
  for (const point of points) {
    const key = `${Math.round(point.x)}:${Math.round(point.y)}`;
    buckets.set(key, (buckets.get(key) || 0) + 1);
  }
  const rows = [...buckets.entries()].map(([key, count]) => {
    const [x, y] = key.split(':').map(Number);
    return { x, y, count };
  });
  const maxCount = Math.max(1, ...rows.map(row => row.count));
  return (
    <div>
      <div className="text-muted-foreground mb-2 flex items-center justify-between text-xs">
        <span>{xLabel}</span>
        <span>{yLabel}</span>
      </div>
      <div className="bg-background grid max-h-[320px] grid-cols-2 gap-2 overflow-auto rounded-md border p-3 sm:grid-cols-3 lg:grid-cols-4">
        {rows.map(row => (
          <div key={`${row.x}:${row.y}`} className="rounded-md border p-3 text-xs">
            <div
              className="bg-primary mb-2 h-2 rounded-full"
              style={{ opacity: 0.2 + 0.8 * (row.count / maxCount) }}
            />
            <div className="font-medium">
              {row.x} {'->'} {row.y}
            </div>
            <div className="text-muted-foreground">
              {row.count} sample{row.count === 1 ? '' : 's'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Display a summary value as text; null or undefined is represented as zero by this presentation helper. */
function StatPill({ label, value }: { /** Visible caption describing this control or metric. */
label: string; /** Preformatted metric value displayed without additional measurement. */
value: unknown }) {
  return (
    <div className="bg-muted/25 rounded-md border p-3">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="mt-1 text-lg font-semibold">{String(value ?? 0)}</div>
    </div>
  );
}

/** Show up to eight model-error rows with sample counts, accepting both camelCase and snake_case response fields. An empty collection displays the supplied explanation. */
function ErrorList({ title, rows, empty }: { /** Visible heading for this panel or collection. */
title: string; /** Error summaries by activity class or bucket; only the first eight are displayed. */
rows: Record<string, unknown>[]; /** Message shown when the selected error summary contains no rows. */
empty: string }) {
  return (
    <div className="rounded-md border">
      <div className="border-b px-3 py-2 text-sm font-medium">{title}</div>
      {rows.length === 0 ? (
        <p className="text-muted-foreground p-3 text-sm">{empty}</p>
      ) : (
        <div className="divide-y">
          {rows.slice(0, 8).map((row, index) => (
            <div
              key={`${row.activity_class || row.bucket || index}`}
              className="grid grid-cols-[1fr_auto] gap-3 p-3 text-sm"
            >
              <div className="min-w-0 truncate">
                {String(row.activity_class || row.bucket || 'Unknown')}
              </div>
              <div className="text-muted-foreground text-right">
                {formatNumber(row.mae_minutes ?? row.maeMinutes, 1)}m
                <span className="ml-2 text-xs">n={String(row.samples ?? row.sampleCount ?? 0)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Combine aggregate errors, activity counts, feedback coverage, and normalized trends into the dashboard summary. Readiness stages are bounded UI heuristics; missing numeric aggregates default to zero and do not establish measured model quality. */
function buildModelImprovement(
  apiAnalytics: TrainingAnalyticsData | null,
  exportedTraining: TrainingExportData | null,
  activities: ActivitySample[]
): ModelImprovementSummary {
  const overview = apiAnalytics?.overview || {};
  const duration = apiAnalytics?.modelError?.duration || {};
  const trainingShape = apiAnalytics?.trainingDataShape || {};
  const trendRows = normalizeTrendRows(duration.trend || []);
  const trend = compareTrend(trendRows);
  const sampleCount = numberOrZero(trainingShape.durationSamples ?? duration.sampleCount);
  const feedbackCount = numberOrZero(trainingShape.feedbackSamples ?? overview.totalFeedback);
  const sessionCount = numberOrZero(trainingShape.sessions ?? overview.totalSessions);
  const energySamples = numberOrZero(trainingShape.energySamples);
  const customFields = numberOrZero(trainingShape.customFields);
  const fieldImpacts = buildFieldImpactRows(apiAnalytics, exportedTraining);

  const stages: LearningStage[] = [
    {
      label: 'Capture',
      value: `${activities.length} activities`,
      detail: 'Scheduled and completed activities create the raw planning context.',
      progress: clamp(percent(Math.min(activities.length, 40), 40), 0, 100),
    },
    {
      label: 'Compare',
      value: `${sampleCount} duration deltas`,
      detail: 'Actual vs estimated duration is the model correction loop.',
      progress: clamp(percent(Math.min(sampleCount, 24), 24), 0, 100),
    },
    {
      label: 'Qualify',
      value: `${feedbackCount + sessionCount} feedback rows`,
      detail: 'Session and feedback rows teach energy, focus, and completion quality.',
      progress: clamp(percent(Math.min(feedbackCount + sessionCount, 30), 30), 0, 100),
    },
    {
      label: 'Generalize',
      value: `${customFields + energySamples} signals`,
      detail: 'Custom fields and energy samples help transfer learning between work types.',
      progress: clamp(percent(Math.min(customFields * 4 + energySamples, 40), 40), 0, 100),
    },
  ];

  const recommendations = buildTrainingRecommendations({
    sampleCount,
    feedbackCount,
    customFields,
    durationMae: numberOrZero(duration.maeMinutes),
    bias: numberOrZero(duration.meanErrorMinutes),
    trendDeltaMinutes: trend.deltaMinutes,
    fieldImpacts,
  });

  return {
    accuracyScore: clamp(numberOrZero(overview.schedulingAccuracy), 0, 100),
    durationMae: numberOrZero(duration.maeMinutes),
    rmse: numberOrZero(duration.rmseMinutes),
    bias: numberOrZero(duration.meanErrorMinutes),
    sampleCount,
    readinessScore: average(stages.map(stage => stage.progress)),
    trendRows,
    trendDeltaMinutes: trend.deltaMinutes,
    trendDeltaPercent: trend.deltaPercent,
    stages,
    fieldImpacts,
    recommendations,
  };
}

/** Accept retained snake/camel error fields, use zero for missing numeric values, and sort by the textual bucket key. This normalization is not a guarantee that arbitrary bucket labels are chronological. */
function normalizeTrendRows(rows: Array<Record<string, unknown>>): TrendPoint[] {
  return rows
    .map((row, index) => {
      const mae = numberOrNull(
        row.mae_minutes ?? row.maeMinutes ?? row.absolute_error_minutes ?? row.absoluteErrorMinutes
      );
      return {
        bucket: String(
          row.bucket || row.week || row.created_at || row.createdAt || `bucket ${index + 1}`
        ),
        mae: mae ?? 0,
        samples: numberOrZero(row.samples ?? row.sampleCount ?? row.count),
        bias: numberOrZero(
          row.mean_error_minutes ?? row.meanErrorMinutes ?? row.error_minutes ?? row.errorMinutes
        ),
      };
    })
    .filter(row => Number.isFinite(row.mae))
    .sort((left, right) => left.bucket.localeCompare(right.bucket));
}

/** Compare unweighted mean MAE in the earlier and recent halves of the ordered buckets. Positive delta means lower recent error; fewer than two buckets or a zero earlier baseline leaves the corresponding comparison unknown. */
function compareTrend(rows: TrendPoint[]): {
  /** Earlier minus recent mean MAE in minutes; positive means improvement, null means insufficient buckets. */
  deltaMinutes: number | null;
  /** Error reduction as a percentage of earlier MAE; null if the earlier baseline is zero or unavailable. */
  deltaPercent: number | null;
} {
  if (rows.length < 2) return { deltaMinutes: null, deltaPercent: null };
  const midpoint = Math.max(1, Math.floor(rows.length / 2));
  const earlier = average(rows.slice(0, midpoint).map(row => row.mae));
  const recent = average(rows.slice(midpoint).map(row => row.mae));
  const delta = earlier - recent;
  return {
    deltaMinutes: delta,
    deltaPercent: earlier > 0 ? (delta / earlier) * 100 : null,
  };
}

/** Use aggregate field usage when raw training is absent; otherwise join exported duration rows to task attributes by ID and accumulate error where each field is present. The resulting association ranking is not causal feature attribution. */
function buildFieldImpactRows(
  apiAnalytics: TrainingAnalyticsData | null,
  exportedTraining: TrainingExportData | null
): FieldImpactRow[] {
  const aggregateRows = buildAggregateFieldRows(apiAnalytics);
  if (aggregateRows.length > 0 && !exportedTraining) return aggregateRows;

  const tasks = Array.isArray(exportedTraining?.tasks) ? exportedTraining.tasks : [];
  const durationRows =
    Array.isArray(exportedTraining?.durationDeltas) && exportedTraining.durationDeltas.length > 0
      ? exportedTraining.durationDeltas
      : exportedTraining?.durationTrainingData || [];
  if (tasks.length === 0 || durationRows.length === 0) return [];

  const taskAttributesById = new Map<string, Record<string, unknown>>();
  for (const task of tasks) {
    const taskId = String(task.id || task.task_id || '');
    if (!taskId) continue;
    const attributes = parseRecord(task.custom_attributes ?? task.customAttributes);
    taskAttributesById.set(taskId, attributes);
  }

  const attributeDefs = Array.isArray(exportedTraining?.attributes)
    ? exportedTraining.attributes
    : [];
  const knownKeys = new Set<string>();
  const groups = new Map<
    string,
    { label: string; samples: number; absError: number; signedError: number; examples: Set<string> }
  >();

  for (const row of durationRows) {
    const taskId = String(row.task_id || row.taskId || '');
    const attributes = taskAttributesById.get(taskId);
    const error = durationError(row);
    if (!attributes || error === null) continue;

    for (const attribute of attributeDefs) {
      const value = readAttributeValue(attributes, attribute);
      if (isBlank(value)) continue;
      const label = String(attribute.name || attribute.attr_id || 'Custom field');
      const key = `defined:${attribute.attr_id || normalizeAttributeKey(label)}`;
      knownKeys.add(String(attribute.attr_id || ''));
      knownKeys.add(normalizeAttributeKey(label));
      addFieldImpact(groups, key, label, value, error);
    }

    for (const [key, value] of Object.entries(attributes)) {
      if (isBlank(value) || !isDisplayableAttributeValue(value)) continue;
      const normalized = normalizeAttributeKey(key);
      if (knownKeys.has(key) || knownKeys.has(normalized)) continue;
      addFieldImpact(groups, `raw:${normalized}`, prettifyKey(key), value, error);
    }
  }

  return [...groups.entries()]
    .map(([key, group]) => ({
      key,
      label: group.label,
      samples: group.samples,
      mae: group.absError / Math.max(1, group.samples),
      bias: group.signedError / Math.max(1, group.samples),
      example: [...group.examples][0] || 'set',
      metricUnit: 'm',
    }))
    .filter(row => row.samples > 0)
    .sort((left, right) => right.mae - left.mae || right.samples - left.samples);
}

/** Adapt aggregate custom-field usage to the shared panel row shape. Here mae carries coverage percent and bias carries numeric-value count, distinguished by metricUnit %, rather than duration-error units. */
function buildAggregateFieldRows(apiAnalytics: TrainingAnalyticsData | null): FieldImpactRow[] {
  const rows = Array.isArray(apiAnalytics?.modelError?.customFields?.usage)
    ? apiAnalytics.modelError.customFields.usage
    : [];
  return rows
    .map(row => {
      const observed = numberOrZero(row.observedValueCount);
      const eligible = Math.max(1, numberOrZero(row.eligibleTaskCount));
      return {
        key: `${row.scope || 'field'}:${row.attrId || row.name}`,
        label: `${row.name || 'Custom field'}${row.scope === 'team' ? ' · team' : ''}`,
        samples: observed,
        mae: percent(observed, eligible),
        bias: numberOrZero(row.numericValueCount),
        example: row.teamName || row.dataType || row.scope || 'field',
        metricUnit: '%',
      };
    })
    .filter(row => row.samples > 0)
    .sort((left, right) => right.mae - left.mae || right.samples - left.samples);
}

/** Produce at most four heuristic suggestions from sample coverage, duration bias/error, trends, and field rows. Suggestions describe these inputs and do not trigger retraining. */
function buildTrainingRecommendations({
  sampleCount,
  feedbackCount,
  customFields,
  durationMae,
  bias,
  trendDeltaMinutes,
  fieldImpacts,
}: {
  /** Number of paired duration observations available for learning. */
  sampleCount: number;
  /** Number of feedback records contributing to evidence readiness. */
  feedbackCount: number;
  /** Number of custom fields available to describe activities. */
  customFields: number;
  /** Mean absolute duration prediction error in minutes. */
  durationMae: number;
  /** Mean signed duration prediction error in minutes. */
  bias: number;
  /** Earlier minus recent mean absolute error; null when comparison is unavailable. */
  trendDeltaMinutes: number | null;
  /** Descriptive error/coverage rows used to suggest evidence improvements. */
  fieldImpacts: FieldImpactRow[];
}): string[] {
  const items: string[] = [];
  if (sampleCount < 12) {
    items.push('Log a dozen completed sessions before trusting duration predictions.');
  } else if (trendDeltaMinutes !== null && trendDeltaMinutes > 0) {
    items.push(
      `Recent duration error is down ${formatNumber(trendDeltaMinutes, 1)}m; keep the current feedback rhythm.`
    );
  }

  if (Math.abs(bias) >= 5) {
    items.push(
      bias > 0
        ? `Actual work is running ${formatNumber(bias, 1)}m over estimate on average; increase estimates for similar activities.`
        : `Actual work is finishing ${formatNumber(Math.abs(bias), 1)}m under estimate on average; reclaim time from padded blocks.`
    );
  }

  if (durationMae >= 20) {
    items.push('Prioritize the highest-error classes and fields before broad retraining.');
  }

  if (customFields === 0) {
    items.push(
      'Add team or activity custom fields so predictions can learn from activity type, client, or work mode.'
    );
  } else if (fieldImpacts[0]) {
    items.push(
      `${fieldImpacts[0].label} is the highest-error custom signal; review its values for better estimation cues.`
    );
  }

  if (feedbackCount < 8) {
    items.push(
      'Capture more completion feedback to connect duration error with focus and energy quality.'
    );
  }

  return items.slice(0, 4).length > 0
    ? items.slice(0, 4)
    : ['Training data is healthy; monitor trend changes after each batch of completed sessions.'];
}

/** Accumulate sample count, absolute/signed duration error, and up to two formatted examples in a mutable field group. */
function addFieldImpact(
  groups: Map<
    string,
    { /** Human-readable field label retained in the accumulator. */ label: string; /** Number of paired duration observations contributing to this field group. */ samples: number; /** Sum of absolute duration errors in minutes, divided by samples for MAE. */ absError: number; /** Sum of signed duration errors in minutes, divided by samples for bias. */ signedError: number; /** At most two formatted example field values for diagnostic context. */ examples: Set<string> }
  >,
  key: string,
  label: string,
  value: unknown,
  error: number
) {
  const current = groups.get(key) || {
    label,
    samples: 0,
    absError: 0,
    signedError: 0,
    examples: new Set<string>(),
  };
  current.samples += 1;
  current.absError += Math.abs(error);
  current.signedError += error;
  if (current.examples.size < 2) current.examples.add(formatAttributeValue(value));
  groups.set(key, current);
}

/** Prefer an explicit duration-error field; otherwise subtract estimate from actual when both are numeric. Missing evidence returns null. */
function durationError(row: Record<string, unknown>): number | null {
  const explicit = numberOrNull(row.error_minutes ?? row.errorMinutes);
  if (explicit !== null) return explicit;
  const actual = numberOrNull(row.actual_duration ?? row.actualDuration);
  const estimated = numberOrNull(row.estimated_duration ?? row.estimatedDuration);
  if (actual === null || estimated === null) return null;
  return actual - estimated;
}

/** Look up a custom-field value by stable ID, normalized name, then original name, using own properties only. The first present property wins even if its value is blank. */
function readAttributeValue(
  attributes: Record<string, unknown>,
  attribute: Record<string, unknown>
): unknown {
  const candidates = [attribute.attr_id, normalizeAttributeKey(attribute.name), attribute.name]
    .map(value => String(value || ''))
    .filter(Boolean);
  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(attributes, key)) return attributes[key];
  }
  return undefined;
}

/** Flatten retained wire/UI activity shapes into aligned analytics samples while preserving missing numbers as null. Generated fallback IDs and labels support display only, not durable identity. */
function normalizeActivities(rawTasks: unknown[]): ActivitySample[] {
  if (!Array.isArray(rawTasks)) return [];
  return rawTasks
    .map((raw, index) => {
      const task = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
      const core = safeRecord(task.core_fields);
      const state = safeRecord(task.state_profile);
      const custom = safeRecord(task.custom_attributes);
      const ai = safeRecord(task.ai_metadata);
      return {
        id: String(task.id || core.id || `activity-${index}`),
        title: String(core.title || task.title || 'Untitled activity'),
        status: String(core.status || task.status || 'todo'),
        bundle: String(task.bundle_name || custom.bundle_name || task.bundle_id || 'Unbundled'),
        project: String(
          task.project_name || custom.project_name || task.project_id || 'No project'
        ),
        assignee: String(
          custom.assigned_to_email ||
            custom.assigned_to_user_id ||
            task.assigned_to_user_id ||
            task.user_id ||
            'Me'
        ),
        priorityScore: priorityToNumber(core.priority ?? task.priority ?? task.priority_score),
        durationMinutes: numberOrNull(
          core.duration_minutes ??
            task.duration_minutes ??
            task.estimatedDuration ??
            task.estimated_duration
        ),
        remainingMinutes: numberOrNull(
          core.remaining_duration_minutes ?? task.remaining_duration ?? task.remainingDuration
        ),
        completionPercent: numberOrNull(
          core.completion_percent ?? task.completion_percent ?? task.completionPercent
        ),
        scheduledStart: stringOrNull(core.do_date ?? task.scheduledStart ?? task.scheduled_start),
        scheduledEnd: stringOrNull(core.do_date_end ?? task.scheduledEnd ?? task.scheduled_end),
        dueDate: stringOrNull(core.due_date ?? task.dueDate ?? task.due_date),
        createdAt: stringOrNull(task.created_at ?? task.createdAt),
        updatedAt: stringOrNull(task.updated_at ?? task.updatedAt),
        requiredP: numberOrNull(state.required_productivity_energy),
        requiredRJ: numberOrNull(state.required_rejuvenation_energy),
        returnP: numberOrNull(state.productivity_return),
        returnRJ: numberOrNull(state.rejuvenation_return),
        resistance: numberOrNull(state.resistance),
        focus: numberOrNull(safeRecord(custom.actual_session).focus_level ?? ai.focus_level),
        emotion: String(
          custom.emotion_word ||
            custom.emotion ||
            custom.emotion_label ||
            ai.predicted_emotion ||
            'Unknown'
        ),
        customAttributes: custom,
      };
    })
    .filter(activity => activity.id);
}

/** Filter using the first available scheduled/updated/created/due timestamp and a rolling day-count window. Undated or invalidly dated records remain included; future valid timestamps are excluded. */
function filterByRange(activities: ActivitySample[], range: TimeRange): ActivitySample[] {
  if (range === 'all') return activities;
  const now = new Date();
  const start = new Date(now);
  const days = range === 'week' ? 7 : range === 'month' ? 31 : range === 'quarter' ? 92 : 366;
  start.setDate(now.getDate() - days);
  return activities.filter(activity => {
    const value =
      activity.scheduledStart || activity.updatedAt || activity.createdAt || activity.dueDate;
    if (!value) return true;
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return true;
    return date >= start && date <= now;
  });
}

/** Build activity-index-aligned base and custom numeric series, joining actual-duration rows by task ID. Preserve null gaps and drop series with no numeric observations so later pairing cannot shift activity alignment. */
function buildNumericSeries(
  activities: ActivitySample[],
  exportedTraining: TrainingExportData | null
): NumericSeries[] {
  const trainingRows = Array.isArray(exportedTraining?.durationTrainingData)
    ? exportedTraining.durationTrainingData
    : [];
  const actualDurationsByTask = new Map<string, number>();
  for (const row of trainingRows) {
    const taskId = String(row?.task_id || '');
    const actual = numberOrNull(row?.actual_duration);
    if (taskId && actual !== null) actualDurationsByTask.set(taskId, actual);
  }

  const base: NumericSeries[] = [
    {
      key: 'duration',
      label: 'Duration',
      values: activities.map(activity => activity.durationMinutes),
    },
    {
      key: 'actual_duration',
      label: 'Actual duration',
      values: activities.map(activity => actualDurationsByTask.get(activity.id) ?? null),
    },
    {
      key: 'remaining',
      label: 'Remaining',
      values: activities.map(activity => activity.remainingMinutes),
    },
    {
      key: 'completion',
      label: 'Completion %',
      values: activities.map(activity => activity.completionPercent),
    },
    {
      key: 'priority',
      label: 'Priority',
      values: activities.map(activity => activity.priorityScore),
    },
    {
      key: 'required_p',
      label: 'Required P',
      values: activities.map(activity => activity.requiredP),
    },
    {
      key: 'required_rj',
      label: 'Required RJ',
      values: activities.map(activity => activity.requiredRJ),
    },
    { key: 'p_return', label: 'P return', values: activities.map(activity => activity.returnP) },
    { key: 'rj_return', label: 'RJ return', values: activities.map(activity => activity.returnRJ) },
    {
      key: 'net_p',
      label: 'Net P',
      values: activities.map(activity => difference(activity.returnP, activity.requiredP)),
    },
    {
      key: 'net_rj',
      label: 'Net RJ',
      values: activities.map(activity => difference(activity.returnRJ, activity.requiredRJ)),
    },
    {
      key: 'resistance',
      label: 'Resistance',
      values: activities.map(activity => activity.resistance),
    },
    { key: 'focus', label: 'Focus', values: activities.map(activity => activity.focus) },
    {
      key: 'hour',
      label: 'Hour',
      values: activities.map(activity => hourOf(activity.scheduledStart)),
    },
    {
      key: 'weekday',
      label: 'Weekday',
      values: activities.map(activity => weekdayOf(activity.scheduledStart)),
    },
  ];

  const customKeys = new Set<string>();
  for (const activity of activities) {
    for (const [key, value] of Object.entries(activity.customAttributes)) {
      if (numberOrNull(value) !== null) customKeys.add(key);
    }
  }
  for (const key of customKeys) {
    base.push({
      key: `custom:${key}`,
      label: prettifyKey(key),
      values: activities.map(activity => numberOrNull(activity.customAttributes[key])),
    });
  }
  return base.filter(item => item.values.some(value => value !== null));
}

/** Evaluate each unique numeric-series pair, drop undefined correlations, and rank by absolute Pearson coefficient. Ranking describes association without assigning causality. */
function buildCorrelations(series: NumericSeries[]) {
  const rows: Array<{ a: string; b: string; aLabel: string; bLabel: string; r: number }> = [];
  for (let a = 0; a < series.length; a += 1) {
    for (let b = a + 1; b < series.length; b += 1) {
      const left = series[a]!;
      const right = series[b]!;
      const r = pearson(left.values, right.values);
      if (r === null) continue;
      rows.push({ a: left.key, b: right.key, aLabel: left.label, bLabel: right.label, r });
    }
  }
  return rows.sort((left, right) => Math.abs(right.r) - Math.abs(left.r));
}

/** Interpret the bounded local correlate/average command grammar against loaded series. Return guidance for unsupported or missing inputs; never evaluate arbitrary code or issue a server query. */
function runAnalysisQuery(
  query: string,
  series: NumericSeries[],
  activities: ActivitySample[]
): string {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return 'Enter a command.';
  if (series.length === 0) return 'No numeric metric series in this range yet.';
  if (normalized.startsWith('correlate')) {
    const [, aKey, bKey] = normalized.split(/\s+/);
    const a = findSeries(series, aKey);
    const b = findSeries(series, bKey);
    if (!a || !b) return `Unknown metric. Available: ${series.map(item => item.key).join(', ')}`;
    const r = pearson(a.values, b.values);
    return r === null
      ? `Not enough paired numeric samples for ${a.label} and ${b.label}.`
      : `corr(${a.label}, ${b.label}) = ${formatNumber(r, 3)} across ${pairedCount(a.values, b.values)} paired samples.`;
  }
  if (normalized.startsWith('average')) {
    const match = normalized.match(/^average\s+(.+?)\s+by\s+(.+)$/);
    if (!match) return 'Use: average duration by bundle';
    const metric = findSeries(series, match[1]?.trim());
    const group = match[2]?.trim() || '';
    if (!metric) return `Unknown metric. Available: ${series.map(item => item.key).join(', ')}`;
    const groups = new Map<string, number[]>();
    activities.forEach((activity, index) => {
      const value = metric.values[index];
      if (value === null || !Number.isFinite(value)) return;
      const key = group.includes('emotion')
        ? activity.emotion
        : group.includes('project')
          ? activity.project
          : group.includes('assignee')
            ? activity.assignee
            : activity.bundle;
      groups.set(key, [...(groups.get(key) || []), value]);
    });
    if (groups.size === 0) return 'No grouped numeric samples.';
    return [...groups.entries()]
      .map(([key, values]) => `${key}: ${formatNumber(average(values), 2)} (n=${values.length})`)
      .join('\n');
  }
  return 'Supported commands: correlate <metric> <metric>; average <metric> by bundle|project|emotion|assignee.';
}

/** Pair same-index finite values up to the shorter series length, omitting pairs with missing or nonfinite observations. */
function zipSeries(
  xSeries?: NumericSeries,
  ySeries?: NumericSeries
): Array<{ /** Finite value of the selected horizontal metric for a paired observation. */ x: number; /** Finite value of the selected vertical metric from the same activity index. */ y: number }> {
  if (!xSeries || !ySeries) return [];
  const length = Math.min(xSeries.values.length, ySeries.values.length);
  const points: Array<{ x: number; y: number }> = [];
  for (let index = 0; index < length; index += 1) {
    const x = xSeries.values[index];
    const y = ySeries.values[index];
    if (x === null || y === null || !Number.isFinite(x) || !Number.isFinite(y)) continue;
    points.push({ x, y });
  }
  return points;
}

/** Bucket paired samples by nearest integer X, compute each bucket’s mean Y/count, and sort X ascending for bar/line rendering. */
function groupByRoundedX(points: Array<{ /** Finite value of the selected horizontal metric for a paired observation. */ x: number; /** Finite value of the selected vertical metric from the same activity index. */ y: number }>) {
  const groups = new Map<number, number[]>();
  for (const point of points) {
    const key = Math.round(point.x);
    groups.set(key, [...(groups.get(key) || []), point.y]);
  }
  return [...groups.entries()]
    .map(([x, values]) => ({ x, y: average(values), count: values.length }))
    .sort((left, right) => left.x - right.x);
}

/** Resolve a metric by exact key, custom-key suffix, or normalized display label; return undefined when none match. */
function findSeries(series: NumericSeries[], key?: string) {
  const normalized = String(key || '').replace(/\s+/g, '_');
  return series.find(
    item =>
      item.key === normalized ||
      item.key.endsWith(`:${normalized}`) ||
      item.label.toLowerCase().replace(/\s+/g, '_') === normalized
  );
}

/** Compute Pearson correlation from at least three finite paired observations. Return null for insufficient pairs or either constant series; missing values are omitted as pairs, never replaced by zero. */
function pearson(aValues: Array<number | null>, bValues: Array<number | null>): number | null {
  const pairs: Array<[number, number]> = [];
  const length = Math.min(aValues.length, bValues.length);
  for (let index = 0; index < length; index += 1) {
    const a = aValues[index];
    const b = bValues[index];
    if (a === null || b === null || !Number.isFinite(a) || !Number.isFinite(b)) continue;
    pairs.push([a, b]);
  }
  if (pairs.length < 3) return null;
  const avgA = average(pairs.map(pair => pair[0]));
  const avgB = average(pairs.map(pair => pair[1]));
  const numerator = pairs.reduce((sum, [a, b]) => sum + (a - avgA) * (b - avgB), 0);
  const denomA = Math.sqrt(pairs.reduce((sum, [a]) => sum + (a - avgA) ** 2, 0));
  const denomB = Math.sqrt(pairs.reduce((sum, [, b]) => sum + (b - avgB) ** 2, 0));
  const denominator = denomA * denomB;
  if (!denominator) return null;
  return numerator / denominator;
}

/** Count positions with finite values in both series using the same pairing rule as the plots. */
function pairedCount(aValues: Array<number | null>, bValues: Array<number | null>): number {
  return zipSeries(
    { key: 'a', label: 'a', values: aValues },
    { key: 'b', label: 'b', values: bValues }
  ).length;
}

/** Compute the mean of finite non-null values; an empty usable collection returns zero for presentation calculations. */
function average(values: Array<number | null>): number {
  const finite = values.filter(
    (value): value is number => value !== null && Number.isFinite(value)
  );
  if (finite.length === 0) return 0;
  return finite.reduce((sum, value) => sum + value, 0) / finite.length;
}

/** Use numeric conversion when present and finite, otherwise return the presentation default zero. */
function numberOrZero(value: unknown): number {
  return numberOrNull(value) ?? 0;
}

/** Convert a part/total ratio to percent only for a positive total; otherwise return zero. */
function percent(part: number, total: number): number {
  return total > 0 ? (part / total) * 100 : 0;
}

/** Bound a supplied numeric value to an inclusive interval; this helper assumes its caller has handled nonfinite input. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Format a Number-converted value to the requested precision, using formatted zero for nonfinite input. */
function formatNumber(value: unknown, digits = 1): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return digits === 0 ? '0' : Number(0).toFixed(digits);
  return numeric.toFixed(digits);
}

/** Format a finite-or-zero value with an explicit positive/negative sign and minute suffix. */
function formatSignedMinutes(value: unknown): string {
  const numeric = numberOrZero(value);
  const prefix = numeric > 0 ? '+' : numeric < 0 ? '-' : '';
  return `${prefix}${formatNumber(Math.abs(numeric), 1)}m`;
}

/** Accept a non-array object without parsing; other shapes become an empty record. */
function safeRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Read a non-array object directly or from JSON text; malformed, blank, array, and scalar inputs become an empty record. */
function parseRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value))
    return value as Record<string, unknown>;
  if (typeof value !== 'string' || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** Convert present values to finite numbers while keeping null, undefined, and empty text as missing. This parser intentionally accepts numeric strings. */
function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** Identify absent values while preserving valid zero and false observations. */
function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** Allow scalar string, number, and boolean examples; nested objects are not expanded into analytics labels. */
function isDisplayableAttributeValue(value: unknown): boolean {
  return ['string', 'number', 'boolean'].includes(typeof value);
}

/** Format a bounded scalar example, preserving boolean meaning and limiting text to 24 characters for the field panel. */
function formatAttributeValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return formatNumber(value, Number.isInteger(value) ? 0 : 1);
  const text = String(value ?? '').trim();
  return text.length > 24 ? `${text.slice(0, 21)}...` : text || 'set';
}

/** Keep absent values null and stringify other supplied values for the analytics projection. */
function stringOrNull(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

/** Recognize both current completed and retained done status spellings. */
function isCompleted(status: string): boolean {
  return ['completed', 'done'].includes(status.toLowerCase());
}

/** Preserve numeric priority or map urgent/high/medium/low to the analytics scale 10/7/5/2. Unknown labels remain missing. */
function priorityToNumber(value: unknown): number | null {
  const numeric = numberOrNull(value);
  if (numeric !== null) return numeric;
  const text = String(value || '').toLowerCase();
  if (text === 'urgent') return 10;
  if (text === 'high') return 7;
  if (text === 'medium') return 5;
  if (text === 'low') return 2;
  return null;
}

/** Subtract two observed values only when both are present; otherwise preserve a null result. */
function difference(a: number | null, b: number | null): number | null {
  if (a === null || b === null) return null;
  return a - b;
}

/** Extract a valid timestamp’s browser-local hour; missing or invalid instants remain null. */
function hourOf(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.getHours() : null;
}

/** Extract a valid timestamp’s browser-local weekday number; missing or invalid instants remain null. */
function weekdayOf(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.getDay() : null;
}

/** Replace underscore separators and capitalize word initials for a metric label. */
function prettifyKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

/** Normalize field names into lowercase ASCII underscore aliases used to join retained custom-attribute representations. */
function normalizeAttributeKey(name: unknown): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Serialize the supplied export into a browser Blob, trigger its historical filename download, and revoke the temporary object URL. This action exposes the selected export to the user’s local filesystem. */
function downloadJson(data: TrainingExportData) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `fluxiplanner-training-data-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
}
